/**
 * Normalize DB/client travel_time_type values for UI (Clients use hourly | flat | none).
 */
export function normalizeShiftTravelType(raw) {
  const t = String(raw ?? "")
    .trim()
    .toLowerCase();
  if (t === "hour" || t === "hours" || t === "hourly") return "hourly";
  if (t === "flat" || t === "flat_rate" || t === "flatrate") return "flat";
  if (t === "none" || t === "") return "none";
  return "none";
}

/** Decimal hours used in Clients → Shift settings → Hourly travel time (must stay in sync). */
export const HOURLY_TRAVEL_DECIMAL_HOURS = [
  0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8,
];

const HOURLY_TRAVEL_DECIMAL_TO_LABEL = new Map([
  [0.5, "30m"],
  [1, "1h"],
  [1.5, "1h 30m"],
  [2, "2h"],
  [2.5, "2h 30m"],
  [3, "3h"],
  [3.5, "3h 30m"],
  [4, "4h"],
  [4.5, "4h 30m"],
  [5, "5h"],
  [5.5, "5h 30m"],
  [6, "6h"],
  [6.5, "6h 30m"],
  [7, "7h"],
  [7.5, "7h 30m"],
  [8, "8h"],
]);

export function parseTravelHoursAmount(raw) {
  if (raw == null || raw === "") return NaN;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  const s = String(raw).trim().replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

/** Snap noisy / string DB values to the nearest client dropdown step (0.5h increments). */
export function snapToHourlyTravelStep(hours) {
  const n = parseTravelHoursAmount(hours);
  if (!Number.isFinite(n) || n <= 0) return n;
  let best = HOURLY_TRAVEL_DECIMAL_HOURS[0];
  let bestD = Math.abs(n - best);
  for (const s of HOURLY_TRAVEL_DECIMAL_HOURS) {
    const d = Math.abs(n - s);
    if (d < bestD) {
      best = s;
      bestD = d;
    }
  }
  // ~2.5 minute tolerance — fixes float noise without jumping between half-hour marks
  if (bestD * 60 <= 2.5) return best;
  return n;
}

/** e.g. 0.5 -> "30m", 1 -> "1h", 1.5 -> "1h 30m" */
export function formatHourlyTravelDurationLabel(amount) {
  const raw = parseTravelHoursAmount(amount);
  if (!Number.isFinite(raw) || raw <= 0) return "";
  // Some DBs store minutes as a whole number (e.g. 30) instead of decimal hours (0.5).
  if (
    Number.isInteger(raw) &&
    raw >= 15 &&
    raw < 60 &&
    raw % 5 === 0
  ) {
    const totalM = raw;
    const h = Math.floor(totalM / 60);
    const m = totalM % 60;
    if (m === 0) return `${h}h`;
    if (h === 0) return `${m}m`;
    return `${h}h ${m}m`;
  }
  const n = snapToHourlyTravelStep(raw);
  if (!Number.isFinite(n) || n <= 0) return "";
  const label = HOURLY_TRAVEL_DECIMAL_TO_LABEL.get(n);
  if (label) return label;
  const totalMinutes = Math.round(n * 60);
  if (totalMinutes <= 0) return "";
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (m === 0) return `${h}h`;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

/** Jobs shift Overview checkbox label from client shift settings. */
export function travelCheckboxLabel(travelMode, amount) {
  const mode = normalizeShiftTravelType(travelMode);
  if (mode === "flat") {
    const x = Number(amount);
    const s = Number.isFinite(x) ? `$${x.toFixed(2)}` : "";
    return s ? `Add ${s} Flat Rate Travel` : "Add Flat Rate Travel";
  }
  if (mode === "hourly") {
    const h = formatHourlyTravelDurationLabel(amount);
    return h ? `Add ${h} Travel Time` : "Add Travel Time";
  }
  return "Add Travel Time";
}

/** Schedule sidebar / pay line suffix (e.g. "$2.00 Flat Travel" or "1h travel time"). */
export function travelPayRateSuffix(travelMode, amount) {
  const mode = normalizeShiftTravelType(travelMode);
  if (mode === "none") return "";
  if (mode === "flat") {
    const x = Number(amount);
    return Number.isFinite(x) ? `$${x.toFixed(2)} Flat Travel` : "Flat Travel";
  }
  const h = formatHourlyTravelDurationLabel(amount);
  return h ? `${h} travel time` : "travel time";
}

export function shiftRowHasTravel(amountRaw, typeRaw) {
  const amt = Number(amountRaw);
  const okAmt =
    amountRaw != null &&
    String(amountRaw).trim() !== "" &&
    Number.isFinite(amt) &&
    amt > 0;
  const mode = normalizeShiftTravelType(typeRaw);
  return okAmt && mode !== "none";
}
